import { useCallback, useMemo } from 'react';
import { useApolloClient, useQuery, ApolloQueryResult, QueryResult } from '@apollo/client';
import { useMsal } from '@azure/msal-react';
import GET_ME from 'graph/user/get-me-query';
import { keyBy, Dictionary } from 'lodash';
import { hasAuthCookies } from 'utilities/auth-cookie-util';
import {
  User,
  UserClassroomPrivileges,
  UserOrganizationPrivileges,
  UserSetting
} from 'types/schema-generated-types';
import { StoreTypename } from '../types';

enum PrivilegedContextScope {
  AND = 'AND',
  OR = 'OR'
}

export type PrivilegedContext = {
  classroomId?: number | number[] | null;
  organizationId?: number | number[] | null;
  scope?: PrivilegedContextScope; // defaults to 'OR'
};

export type PrivilegesCache = {
  getClassroomPrivilegesByClassroomId: (classroomId: number) => string[];
  getOrgPrivilegesByOrgId: (organizationId: number) => string[];
  getPrivilegedClassroomIds: (privilege: string) => number[];
  getPrivilegedOrgIds: (privilege: string) => number[];
  isPrivileged: (privileges: string[], context?: PrivilegedContext) => boolean;
};

export type UserStoreState = {
  me?: User;
  privilegesCache: PrivilegesCache;
  settings: UserSetting[];
  isLoading: boolean;
};

export type UserStoreDispatch = {
  setMe: (me: User) => void;
  refetchUser: (
    variables?: Record<string, any> | undefined
  ) => Promise<ApolloQueryResult<{ me: User }>>;
};
export type UserStore = [UserStoreState, UserStoreDispatch];

const buildAllPrivileges = (me?: User): string[] => {
  if (!me) return [];

  return [
    ...(me.classroomPrivileges ?? []).reduce(
      (acc: string[], x) => [...acc, ...(x.privileges ?? [])],
      []
    ),
    ...(me.organizationPrivileges ?? []).reduce(
      (acc: string[], x) => [...acc, ...(x.privileges ?? [])],
      []
    )
  ];
};

const hasPrivilege = (privileges: string[]) => (requiredPrivilege: string): boolean => {
  return privileges.includes(requiredPrivilege);
};

const aggregatePrivileges = (
  privilegeMap: Dictionary<UserClassroomPrivileges | UserOrganizationPrivileges>,
  ids: number[]
): string[] =>
  ids.reduce((acc, id) => {
    const privileges = privilegeMap[id]?.privileges;
    return privileges ? [...acc, ...privileges] : acc; // Don't think we care about duplicates.
  }, [] as string[]);

export const getDefaultUserState = (): any => ({ __typename: StoreTypename.USER });

const useUserStore = (): UserStore => {
  const client = useApolloClient();
  const { accounts } = useMsal();

  const { data, loading, refetch }: QueryResult<{ me: User }> = useQuery(GET_ME, {
    skip: !accounts.length && !hasAuthCookies()
  });

  const allPrivileges = useMemo(() => buildAllPrivileges(data?.me), [data]);
  const classroomPrivilegesMap = useMemo(
    () => keyBy(data?.me?.classroomPrivileges ?? [], 'classroomId'),
    [data]
  );
  const orgPrivilegesMap = useMemo(
    () => keyBy(data?.me?.organizationPrivileges ?? [], 'organizationId'),
    [data]
  );

  const setMe = useCallback(
    (me: User): void => {
      client.writeQuery({ query: GET_ME, data: { me } });
    },
    [client]
  );

  const getClassroomPrivilegesByClassroomId = useCallback(
    (classroomId: number | number[]): string[] => {
      if (Array.isArray(classroomId)) {
        return aggregatePrivileges(classroomPrivilegesMap, classroomId);
      }

      return classroomPrivilegesMap[classroomId]?.privileges ?? [];
    },
    [classroomPrivilegesMap]
  );

  const getOrgPrivilegesByOrgId = useCallback(
    (organizationId: number | number[]): string[] => {
      if (Array.isArray(organizationId)) {
        return aggregatePrivileges(orgPrivilegesMap, organizationId);
      }

      return orgPrivilegesMap[organizationId]?.privileges ?? [];
    },
    [orgPrivilegesMap]
  );

  const getPrivilegedClassroomIds = useCallback(
    (privilege: string): number[] => {
      if (!data?.me?.classroomPrivileges) return [];
      return data.me.classroomPrivileges.reduce((acc: number[], classroomPrivilege) => {
        if (!classroomPrivilege.privileges?.includes(privilege)) return acc;
        return [...acc, classroomPrivilege.classroomId];
      }, []);
    },
    [data]
  );

  const getPrivilegedOrgIds = useCallback(
    (privilege: string): number[] => {
      if (!data?.me?.organizationPrivileges) return [];
      return data.me.organizationPrivileges.reduce((acc: number[], organizationPrivilege) => {
        if (!organizationPrivilege.privileges?.includes(privilege)) return acc;
        return [...acc, organizationPrivilege.organizationId];
      }, []);
    },
    [data]
  );

  const settings = useMemo(() => data?.me?.settings?.filter(s => s.app?.name === 'pli') ?? [], [
    data
  ]);

  const isPrivileged = useCallback(
    (
      privileges: string[] = [],
      { organizationId, classroomId, scope = PrivilegedContextScope.OR }: PrivilegedContext = {}
    ): boolean => {
      if (!privileges.length) return true;
      const searchMethod = scope === PrivilegedContextScope.OR ? 'some' : 'every';

      if (organizationId && classroomId) {
        const orgPrivileges = getOrgPrivilegesByOrgId(organizationId);
        const classroomPrivileges = getClassroomPrivilegesByClassroomId(classroomId);
        const isOrgPrivileged = privileges.some(hasPrivilege(orgPrivileges));
        const isClassroomPrivileged = privileges.some(hasPrivilege(classroomPrivileges));

        // always OR - would not make sense to do 'every' because privileges would be different
        return isOrgPrivileged || isClassroomPrivileged;
      }

      if (organizationId) {
        const orgPrivileges = getOrgPrivilegesByOrgId(organizationId);

        return privileges[searchMethod](hasPrivilege(orgPrivileges));
      }

      if (classroomId) {
        const classroomPrivileges = getClassroomPrivilegesByClassroomId(classroomId);

        return privileges[searchMethod](hasPrivilege(classroomPrivileges));
      }

      // TODO: fallback - this may need to be more strict
      return privileges[searchMethod](hasPrivilege(allPrivileges));
    },
    [getClassroomPrivilegesByClassroomId, getOrgPrivilegesByOrgId, allPrivileges]
  );

  const privilegesCache = useMemo(
    () => ({
      getClassroomPrivilegesByClassroomId,
      getPrivilegedClassroomIds,
      getOrgPrivilegesByOrgId,
      getPrivilegedOrgIds,
      isPrivileged
    }),
    [
      getClassroomPrivilegesByClassroomId,
      getPrivilegedClassroomIds,
      getOrgPrivilegesByOrgId,
      getPrivilegedOrgIds,
      isPrivileged
    ]
  );

  return [
    {
      me: data?.me,
      privilegesCache,
      settings,
      isLoading: loading
    },
    {
      setMe,
      refetchUser: refetch
    }
  ];
};

export default useUserStore;
